import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOrganization, useUser, useUserRoles } from "../../../../../hooks/query";
import { useApiResponseParser } from "../../../../../hooks";

import type { IPrimaryAdminInvite } from "../../../../../interfaces";

type CreateOrganizationInput = {
    Name: string;
    ShortName?: string;
    Description?: string;
    Website?: string;
    IsActive?: boolean;
    LogoFile?: unknown;
    LogoUrl?: string;
};

export default function useCreateOrganization() {
    const navigate = useNavigate();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useCreate, useAddUser } = useOrganization();
    const { useCreateCognito } = useUser();
    const { useSync } = useUserRoles();

    const createOrganizationSvc = useCreate();
    const createCognitoSvc = useCreateCognito();
    const addUserToOrgSvc = useAddUser();
    const syncRolesSvc = useSync();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const isPending =
        createOrganizationSvc.isPending ||
        createCognitoSvc.isPending ||
        addUserToOrgSvc.isPending ||
        syncRolesSvc.isPending;

    const handleCreateOrganization = async (
        data: CreateOrganizationInput,
        primaryAdmin: IPrimaryAdminInvite,
    ) => {
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const { LogoFile: _logoFile, LogoUrl: _logoUrl, ...payload } = data;
            const organization = await createOrganizationSvc.mutateAsync(payload);
            const organizationId = organization?.Id;

            if (!organizationId) {
                setErrorMessage("Organization was created but no id was returned.");
                return;
            }

            if (!primaryAdmin.SkipInvite) {
                const userResponse = await createCognitoSvc.mutateAsync({
                    FirstName: primaryAdmin.FirstName.trim(),
                    LastName: primaryAdmin.LastName.trim(),
                    Email: primaryAdmin.Email.trim(),
                    OrganizationId: organizationId,
                    RoleId: primaryAdmin.RoleId,
                });

                const userId = userResponse?.userId || userResponse?.UserId;
                if (!userId) {
                    setErrorMessage(
                        "Organization created, but the primary admin user id was missing. Add them from Users.",
                    );
                    setTimeout(() => navigate("/admin/organizations"), 1500);
                    return;
                }

                await addUserToOrgSvc.mutateAsync({
                    UserId: userId,
                    OrganizationId: organizationId,
                    RoleId: primaryAdmin.RoleId,
                });

                await syncRolesSvc.mutateAsync({
                    UserId: userId,
                    RoleIds: [primaryAdmin.RoleId],
                    OrganizationId: organizationId,
                });

                setSuccessMessage("Organization created and primary admin invited.");
            } else {
                setSuccessMessage("Organization created successfully.");
            }

            setTimeout(() => {
                navigate("/admin/organizations");
            }, 1000);
        } catch (error) {
            console.error("Failed to create organization", error);
            setErrorMessage(parseErrorResponseData(error) || "Failed to create organization. Please try again.");
        }
    };

    return {
        isPending,
        errorMessage,
        successMessage,
        handleCreateOrganization,
    };
}
